import { Component, forceUpdate, h, Prop, State, Watch } from "@stencil/core";
import { IChatData, IError, IMessage, sendMessage } from "../../backend/chatService";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey, TranslationOptions } from "../../utils/loadTranslations";
import { debug, parseLinks } from "../../utils/utils";

/**
 * Ensures a valid ISO date string format for chat messages
 * If the input is a valid date string, returns it as is
 * Otherwise, returns a new ISO date string
 */
const ensureValidDateString = (
    dateStr: string | undefined,
    component?: { debug?: boolean; constructor?: { name?: string } }
): string => {
    if (!dateStr) {
        console.warn("[AiChat] No date string provided, using current date");
        return new Date().toISOString();
    }

    try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
            if (component) {
                debug.log(component, "Successfully parsed date:", {
                    input: dateStr,
                    parsed: date.toISOString()
                });
            }
            return date.toISOString();
        }
        console.warn("[AiChat] Invalid date (NaN):", {
            input: dateStr,
            parsed: date
        });
    } catch (error) {
        console.error("[AiChat] Failed to parse date:", {
            input: dateStr,
            error: error.message
        });
    }
    return new Date().toISOString();
};

@Component({
    tag: "ai-chat",
    styleUrl: "ai-chat.scss",
    shadow: false
})
export class AiChat {
    /** Authentication token for API requests */
    @Prop() readonly token: string;

    /** Chat data containing messages and session information */
    @Prop({ mutable: true }) data: IChatData;

    /** The unique identifier of the case */
    @Prop() readonly caseId: string;

    /** Current status of the case (e.g., 'open', 'closed', 'taken') */
    @Prop() readonly caseStatus: string;

    /** Error state passed from wrapper */
    @Prop() readonly error: IError | null = null;

    /** Enable debug logging */
    @Prop() readonly debug = false;

    @State() messageText = "";
    @State() chatMessages: IMessage[] = [];
    @State() isAiTyping = false;
    @State() translationsLoaded = false;

    private i18nSubscriberId: string;
    private caseTakenMessage: string;
    private chatRef?: HTMLElement & {
        showError: (config: {
            title?: string;
            message: string;
            icon?: string;
            showCloseButton?: boolean;
            closeButtonText?: string;
        }) => void;
        hideError: () => void;
    };

    /** Current locale for translations */
    @Prop({ reflect: true, mutable: true }) locale: string;

    @Watch("error")
    handleErrorChange(newError: IError | null, oldError: IError | null) {
        debug.log(this, "Error prop changed:", { newError, oldError });
        if (newError) {
            debug.log(this, "Showing error via ux-chat:", {
                title: newError.errorCode ? `Error ${newError.errorCode}` : "Error",
                message: newError.errorMessage
            });
            if (this.chatRef) {
                this.chatRef.showError({
                    title: newError.errorCode ? `Error ${newError.errorCode}` : "Error",
                    message: newError.errorMessage,
                    icon: "warning",
                    showCloseButton: false
                });
            } else {
                console.warn("[AiChat] Cannot show error - chatRef not set");
            }
        } else if (oldError) {
            debug.log(this, "Hiding error");
            if (this.chatRef) {
                this.chatRef.hideError();
            } else {
                console.warn("[AiChat] Cannot hide error - chatRef not set");
            }
        }
    }

    componentDidLoad() {
        debug.log(this, "Component loaded, chatRef exists:", !!this.chatRef);
        if (this.error) {
            this.handleErrorChange(this.error, null);
        }
    }

    private handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Enter" && (event.target as HTMLInputElement).value) {
            const value = (event.target as HTMLInputElement).value;

            if (value.trim()) {
                const customEvent = new CustomEvent("uxMessageSend", {
                    detail: value.trim()
                });
                void this.handleMessageSend(customEvent);
            }
        }
    };

    private async handleMessageSend(event: CustomEvent<string>) {
        const messageText = event.detail;

        // Create message with correct schema
        const userMessage: IMessage = {
            source: "user",
            message: messageText,
            timestamp: new Date().toISOString()
        };

        debug.log(this, "Sending message to server:", userMessage);

        // Set typing state and send to server
        this.isAiTyping = true;

        try {
            // Update the local messages array with the user message immediately
            this.data = {
                ...this.data,
                messages: [...this.data.messages, userMessage]
            };

            const aiResponse = await sendMessage(this.caseId, this.data.sessionId, userMessage);
            debug.log(this, "Received response from server:", aiResponse);

            // Ensure the AI response has a valid timestamp
            const validatedResponse = {
                ...aiResponse,
                timestamp: ensureValidDateString(aiResponse.timestamp, this)
            };

            // Update the local messages array with the AI response
            this.data = {
                ...this.data,
                messages: [...this.data.messages, validatedResponse]
            };

            // Remove typing indicator and clear any existing errors
            this.isAiTyping = false;
            this.handleErrorChange(null, this.error);
        } catch (error) {
            console.error("Error getting AI response:", error);
            this.isAiTyping = false;
            this.handleErrorChange(error, this.error);
        }
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => {
            this.caseTakenMessage = i18nProvider.t("ai-in-service:systemMessage.caseTaken");
            debug.log(this, "Loaded caseTakenMessage:", this.caseTakenMessage);

            // Once translations are ready, add the system message
            if (this.caseStatus === "taken") {
                debug.log(this, 'Case status is "taken" at load. Adding system message (after translations are ready).');
                void this.addCaseTakenMessage();
            }
            this.translationsLoaded = true;
            forceUpdate(this);
        });

        document.addEventListener("caseStatusUpdated", this.handleCaseStatusUpdated.bind(this) as unknown as EventListener);
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        document.removeEventListener("caseStatusUpdated", this.handleCaseStatusUpdated.bind(this) as unknown as EventListener);
    }

    async componentWillLoad() {
        // Get initial locale from i18nProvider
        const { language } = i18nProvider;
        this.locale = language;
        await this.loadTranslations();
    }

    @Watch("locale")
    async onLocaleChange() {
        const currentLanguage = i18nProvider.language;
        // Only update if the base language part is different
        const currentBase = currentLanguage.split("-")[0];
        const newBase = this.locale.split("-")[0];

        if (currentBase !== newBase) {
            await this.loadTranslations();
        }
    }

    private async loadTranslations() {
        try {
            const locale = await loadTranslations(this.locale);
            await i18nProvider.changeLanguage(locale);
            // Don't update this.locale here to prevent loops
        } catch (error) {
            console.error("Failed to load or change translations:", error);
        }
    }

    private renderSystemMessage = (message: IMessage) => {
        // Parse links in the message content for both system and bot messages
        if (message.source === "system" || message.source === "bot") {
            const parsedMessage = parseLinks(message.message);
            return <span innerHTML={parsedMessage}></span>;
        }
        return message.message;
    };

    private handleCaseStatusUpdated(event: CustomEvent<string>) {
        if (event.detail === "taken") {
            debug.log(this, 'Case status changed to "taken", adding system message.');
            void this.addCaseTakenMessage();
        }
    }

    private async addCaseTakenMessage() {
        if (!this.caseTakenMessage || this.caseTakenMessage === "systemMessage.caseTaken") {
            console.warn("[AiChat] Translations not ready, retrying in 100ms...");

            // Retry after a short delay
            setTimeout(() => {
                this.caseTakenMessage = i18nProvider.t("ai-in-service:systemMessage.caseTaken");
                this.addCaseTakenMessage();
            }, 100);

            return;
        }

        // "caseTaken" message already exists. Skipping.
        if (this.chatMessages.some((msg) => msg.message === this.caseTakenMessage)) {
            return;
        }

        const caseTakenMessage: IMessage = {
            message: this.caseTakenMessage,
            source: "system",
            timestamp: ensureValidDateString(new Date().toISOString(), this)
        };

        this.chatMessages = [...this.chatMessages, caseTakenMessage];
        debug.log(this, "caseTakenMessage added:", this.caseTakenMessage);
        forceUpdate(this);
    }

    render() {
        const t = (key: TranslationKey, options: TranslationOptions = { returnObjects: true }) =>
            i18nProvider.t(`ai-in-service:${String(key)}`, {
                ...options,
                interpolation: { escapeValue: false }
            });

        if (!this.translationsLoaded) {
            return <div class="loading-state">{t("loading.translations")}</div>;
        }

        // Ensure all messages have valid timestamps
        const validatedMessages =
            this.data.messages?.map((msg) => ({
                ...msg,
                timestamp: ensureValidDateString(msg.timestamp, this)
            })) || [];

        return (
            <div>
                <ux-chat
                    ref={(el) => (this.chatRef = el)}
                    typing={this.isAiTyping ? `${t("name.aiAssistant")} ${t("message.isTyping")}` : undefined}
                >
                    {[...this.chatMessages, ...validatedMessages].map((msg, index) => (
                        <ux-chat-item
                            key={index}
                            source={msg.source as "system" | "bot" | "user"}
                            name={msg.source === "bot" || msg.source === "system" ? t("name.aiAssistant") : t("name.user")}
                            date={msg.timestamp}
                        >
                            <ux-copy>{this.renderSystemMessage(msg)}</ux-copy>
                            {msg.source === "system" && msg.message === this.caseTakenMessage && (
                                <ux-button id={`feedback-button-${index}`}>{t("systemMessage.feedbackButtonLabel")}</ux-button>
                            )}
                        </ux-chat-item>
                    ))}
                    <ux-chat-message-bar
                        placeholder={t("messageBar.placeholder")}
                        slot="bar"
                        onUxMessageSend={(event) => this.handleMessageSend(event)}
                        onKeyPress={this.handleKeyPress}
                        disabled={this.caseStatus === "closed"}
                    ></ux-chat-message-bar>
                </ux-chat>
            </div>
        );
    }
}
